import * as React from "react";
import {
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import {
  CloseIcon,
  FilterAltOffIcon,
  ManageSearchIcon,
  SendIcon,
} from "../Icons/Icons";
import { ButtonCore } from "../ButtonCore/ButtonCore";

const useStyles = makeStyles({
  customScrollbar: {
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d4d4d4c3",
      borderRadius: "8px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#d8d8d8",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  },
});

interface DrawerFilterReportProps {
  children: React.ReactNode;
  onClear: () => void;
  onApply: () => void;
}

export const DrawerFilterReport = ({
  children,
  onClear,
  onApply,
}: DrawerFilterReportProps) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const classes = useStyles();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleApply = () => {
    onApply();
    handleClose();
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Abrir filtros do relatório">
          <Button
            endIcon={<ManageSearchIcon />}
            variant="contained"
            color="primary"
            onClick={handleOpen}
            sx={{
              borderRadius: "6px",
              marginLeft: "6px",
            }}
          >
            Abrir Filtro
          </Button>
        </Tooltip>

        <Tooltip title="Limpar filtros">
          <Button
            color="error"
            variant="outlined"
            onClick={onClear}
            sx={{ marginLeft: "2px" }}
          >
            Limpar
            <FilterAltOffIcon fontSize="small" color="error" />
          </Button>
        </Tooltip>
      </Box>

      <Modal closeAfterTransition open={open} onClose={handleClose}>
        <Fade in={open}>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: { xs: "100%", sm: 680, md: 900 },
              maxWidth: "100%",
              height: "100%",
              boxShadow: 12,
              background: theme.palette.background.paper,
              borderRadius: "12px 0 0 12px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                padding: "20px 24px 16px",
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: "1.15rem" }}
                >
                  Filtros do Relatório
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginTop: "2px",
                    fontSize: "13px",
                  }}
                >
                  Configure os filtros para gerar o relatório desejado.
                </Typography>
              </Box>

              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.action.active, 0.04),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.action.active, 0.1),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              className={classes.customScrollbar}
              sx={{
                flex: 1,
                overflow: "auto",
                padding: "16px 24px",
              }}
            >
              {children}
            </Box>

            <Box
              sx={{
                padding: "16px 24px",
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                display: "flex",
                gap: 1,
              }}
            >
              <ButtonCore
                endIcon={<FilterAltOffIcon />}
                sx={{ width: "30%" }}
                title="Limpar"
                variant="outlined"
                onClick={onClear}
                color="error"
              />
              <ButtonCore
                endIcon={<SendIcon />}
                sx={{ width: "70%" }}
                title="Gerar Relatório"
                variant="contained"
                onClick={handleApply}
              />
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
